import React, { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Save, X, MessageCircle, ChevronUp, ChevronDown, Upload, FileDown, Loader2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import PageLinkSelect from "./PageLinkSelect";
import { toast } from "sonner";

const emptyFaq = { question: "", answer: "", order: 0, category: "", link_url: "", link_label: "" };

const FAQ_CATEGORIES = [
  "About CASS", "Pricing & Getting Started", "Working With Us", "Bookkeeping", "Payroll", "VAT",
  "Self Assessment & Personal Tax", "Limited Company & Corporation Tax", "Business Growth & Planning",
  "Xero & Cloud Software", "Making Tax Digital", "Switching Accountants", "Starting a Business", "General",
];

const csvEscape = (s) => `"${(s || "").replace(/"/g, '""')}"`;
const downloadCsv = (rows, filename) => {
  const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\r\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
};
const downloadTemplate = () => downloadCsv([
  ["Question", "Answer", "Category", "Order"],
  ["Do you help businesses switch to Xero?", "Yes — as a Xero Platinum Partner we handle the full migration from your current software, including setup, data transfer and training.", "Xero & Cloud Software", "1"],
  ["What areas do you cover?", "We're based in Tamworth and work with businesses across the UK, thanks to cloud accounting.", "About CASS", "2"],
], "cassie-faq-import-template.csv");

export default function ChatFaqsManager() {
  const [faqs, setFaqs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [openCats, setOpenCats] = useState({});
  const fileRef = useRef(null);

  const load = () => adminApi.chatFaqs.list().then(setFaqs).catch(() => setFaqs([]));
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...emptyFaq, order: faqs.length + 1 });
  const exportCsv = () => {
    const rows = [["Question", "Answer", "Category", "Order"], ...faqs.map((f) => [f.question, f.answer, f.category || "", String(f.order ?? "")])];
    downloadCsv(rows, "cassie-faqs.csv");
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this Q&A?")) return;
    await adminApi.chatFaqs.remove(id);
    toast.success("Q&A deleted");
    load();
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminApi.chatFaqs.importCsv(fd);
      toast.success(`Imported ${res.imported} Q&A${res.imported === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed. Please check the file format.");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const move = async (faq, dir, catItems) => {
    const i = catItems.findIndex((f) => f.id === faq.id);
    const j = i + dir;
    if (j < 0 || j >= catItems.length) return;
    await adminApi.chatFaqs.update(catItems[i].id, { ...catItems[i], order: catItems[j].order });
    await adminApi.chatFaqs.update(catItems[j].id, { ...catItems[j], order: catItems[i].order });
    load();
  };
  const toggleCat = (cat) => setOpenCats((o) => ({ ...o, [cat]: !o[cat] }));

  if (editing) return <FaqEditor faq={editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-display font-extrabold text-2xl text-navy flex items-center gap-2"><MessageCircle size={22} className="text-orange" /> Cassie Knowledge Base</h1>
        <div className="flex items-center gap-2">
          <button data-testid="faq-template-btn" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 border border-slate-300 text-navy hover:bg-slate-50 font-semibold text-sm px-4 py-2.5 rounded-full transition-colors"><FileDown size={16} /> Template</button>
          {faqs.length > 0 && <button data-testid="faq-export-btn" onClick={exportCsv} className="inline-flex items-center gap-1.5 border border-slate-300 text-navy hover:bg-slate-50 font-semibold text-sm px-4 py-2.5 rounded-full transition-colors"><FileDown size={16} /> Export</button>}
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleImport} className="hidden" />
          <button data-testid="faq-import-btn" onClick={() => fileRef.current?.click()} disabled={importing} className="inline-flex items-center gap-1.5 border border-slate-300 text-navy hover:bg-slate-50 disabled:opacity-60 font-semibold text-sm px-4 py-2.5 rounded-full transition-colors">
            {importing ? <><Loader2 size={16} className="animate-spin" /> Importing...</> : <><Upload size={16} /> Import CSV</>}
          </button>
          <button data-testid="faq-add-btn" onClick={startNew} className="inline-flex items-center gap-1.5 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-5 py-2.5 rounded-full transition-colors"><Plus size={16} /> Add Q&A</button>
        </div>
      </div>
      <p className="text-slate-500 text-sm mb-8">These question &amp; answer pairs train Cassie, your website chatbot. She understands questions asked in different words, so you don't need to list every phrasing — just the likely questions and the answer you'd like her to give. She also uses your live site content (services, pricing, meeting room, etc.). To bulk-add, download the <strong>Template</strong>, fill in your Question &amp; Answer columns, then <strong>Import CSV</strong>.</p>

      <div className="space-y-3">
        {faqs.length === 0 && <p className="text-sm text-slate-400">No Q&A yet. Add your first likely question and answer.</p>}
        {(() => {
          const grouped = {};
          faqs.forEach((f) => { const c = f.category || "General"; (grouped[c] = grouped[c] || []).push(f); });
          const known = FAQ_CATEGORIES.filter((c) => grouped[c]);
          const extra = Object.keys(grouped).filter((c) => !FAQ_CATEGORIES.includes(c));
          const cats = [...known, ...extra];
          return cats.map((cat) => {
            const items = grouped[cat];
            const isOpen = !!openCats[cat];
            return (
              <div key={cat} data-testid={`faq-cat-${cat}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleCat(cat)}
                  data-testid={`faq-cat-toggle-${cat}`}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50 transition-colors"
                >
                  <span className="flex items-center gap-2.5">
                    <span className="font-display font-bold text-navy">{cat}</span>
                    <span className="text-[11px] font-bold text-orange bg-orange/10 px-2 py-0.5 rounded-full">{items.length}</span>
                  </span>
                  {isOpen ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
                </button>
                {isOpen && (
                  <div className="border-t border-slate-100 divide-y divide-slate-100">
                    {items.map((f, idx) => (
                      <div key={f.id} data-testid={`faq-row-${f.id}`} className="p-5">
                        <div className="flex items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-navy">{f.question || "Untitled question"}</p>
                            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{f.answer}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => move(f, -1, items)} disabled={idx === 0} className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30" title="Move up"><ChevronUp size={16} /></button>
                            <button onClick={() => move(f, 1, items)} disabled={idx === items.length - 1} className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30" title="Move down"><ChevronDown size={16} /></button>
                            <button onClick={() => setEditing(f)} className="text-sm font-semibold text-orange hover:text-orange-dark px-3 py-1.5">Edit</button>
                            <button data-testid={`faq-delete-${f.id}`} onClick={() => remove(f.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
}

function FaqEditor({ faq, onDone, onCancel }) {
  const [form, setForm] = useState({ ...emptyFaq, ...faq });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) { toast.error("Please fill in both the question and answer"); return; }
    setSaving(true);
    try {
      if (faq.id) await adminApi.chatFaqs.update(faq.id, form);
      else await adminApi.chatFaqs.create(form);
      toast.success("Q&A saved");
      onDone();
    } catch { toast.error("Could not save"); setSaving(false); }
  };

  return (
    <div>
      <button onClick={onCancel} className="text-sm font-semibold text-slate-500 hover:text-navy mb-4 inline-flex items-center gap-1"><X size={15} /> Back</button>
      <h1 className="font-display font-extrabold text-2xl text-navy mb-6">{faq.id ? "Edit Q&A" : "New Q&A"}</h1>
      <div className="max-w-2xl space-y-5 bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div>
          <label className="block text-sm font-semibold text-navy mb-1.5">Likely question</label>
          <input data-testid="faq-question" value={form.question} onChange={(e) => set("question", e.target.value)} className={inputCls} placeholder="e.g. Do you help businesses switch to Xero?" />
          <p className="text-xs text-slate-400 mt-1">Cassie matches questions by meaning, so you only need one clear version of the question.</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-navy mb-1.5">Answer</label>
          <textarea data-testid="faq-answer" rows={5} value={form.answer} onChange={(e) => set("answer", e.target.value)} className={inputCls} placeholder="The answer you'd like Cassie to give." />
        </div>
        <div>
          <label className="block text-sm font-semibold text-navy mb-1.5">Category</label>
          <select data-testid="faq-category" value={form.category || ""} onChange={(e) => set("category", e.target.value)} className={inputCls}>
            <option value="">General</option>
            {FAQ_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <p className="text-xs text-slate-400 mt-1">Groups this question on the public FAQ page (/faqs). Leave as General if unsure.</p>
        </div>
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 space-y-4">
          <p className="text-sm font-semibold text-navy">Optional link button</p>
          <p className="text-xs text-slate-400 -mt-2">Turn this answer into a call-to-action by linking it to a page on your website. Leave blank for no button.</p>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Button text</label>
            <input data-testid="faq-link-label" value={form.link_label || ""} onChange={(e) => set("link_label", e.target.value)} className={inputCls} placeholder='e.g. Learn more about us' />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Links to page</label>
            <PageLinkSelect testId="faq-link-url" value={form.link_url} onChange={(v) => set("link_url", v)} className={inputCls} />
          </div>
        </div>
        <button data-testid="faq-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold px-6 py-2.5 rounded-full transition-colors">
          <Save size={16} /> {saving ? "Saving..." : "Save Q&A"}
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";
